import { t } from '@bia-auction/shared-i18n';
import { IDEMPOTENCY_HEADER, errorEnvelopeSchema } from '@bia-auction/shared-schema';
import { API_BASE } from './config';
import { ensureAccess, refreshAccess } from './token-store';

/** A REST error carrying the server's status + envelope message, the correlation id
 * (M9, ADR 0023) when supplied — surfaced for support in the error boundary — and the
 * envelope's stable `error` label (`code`, M10) so callers can localize coded outcomes
 * (e.g. the refund flow's `RefundBlockedTopBidder`) instead of showing the English message. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly requestId?: string,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Server `error` labels whose copy the client owns (M19). The admin app is tr-only, so a raw
 * English server message is not a fallback here — it is the wrong language outright. Lazy so
 * each resolves at failure time.
 */
const LOCALIZED_ERRORS: Record<string, (() => string) | undefined> = {
  ResetToken: () => t('reset.invalidToken'),
  // The single-use Turnstile token often expires between challenge and submit, so this is an
  // ordinary outcome on the forgot-password form rather than an edge case.
  Turnstile: () => t('common.turnstileFailed'),
};

async function toError(res: Response): Promise<never> {
  let message = res.statusText || `HTTP ${res.status}`;
  let requestId = res.headers.get('x-request-id') ?? undefined;
  // Rate-limit rejections carry a fixed English server message (there is no per-request locale
  // on an unauthenticated endpoint), so they are localized by status rather than by label.
  if (res.status === 429) throw new ApiError(429, t('common.rateLimited'), requestId, 'RateLimited');
  let code: string | undefined;
  try {
    const parsed = errorEnvelopeSchema.safeParse(await res.json());
    if (parsed.success) {
      message = LOCALIZED_ERRORS[parsed.data.error]?.() ?? parsed.data.message;
      code = parsed.data.error;
      if (parsed.data.requestId) requestId = parsed.data.requestId;
    }
  } catch {
    // non-JSON body — keep the status text
  }
  throw new ApiError(res.status, message, requestId, code);
}

export interface ApiOptions {
  method?: string;
  body?: unknown;
  /** Send the Bearer token (default true); set false for login/refresh. */
  auth?: boolean;
  /** Abort signal (e.g. to cancel a superseded autosave). */
  signal?: AbortSignal;
  /**
   * Idempotency key for a retry-unsafe POST (M19 — bid placement). One key per user gesture,
   * reused for every retry of it, so a resend whose first response was lost replays the
   * original result instead of committing a second bid.
   */
  idempotencyKey?: string;
}

/** A validator (any Zod schema satisfies this structurally) for runtime response parsing. */
export interface ResponseSchema<T> {
  parse: (data: unknown) => T;
}

/**
 * Typed fetch against the Worker (ADR 0019). Attaches the Bearer token, parses the shared
 * error envelope, and on a 401 refreshes once and retries before giving up (then clears the
 * session). A 204 resolves to `undefined`. When a `schema` is passed the JSON body is
 * **runtime-validated** against the shared Zod contract before it reaches components (AGENTS
 * §runtime-validated shared contract); without one the body is returned as `T` (used for acks
 * whose value the UI ignores).
 */
export async function api<T>(
  path: string,
  opts: ApiOptions = {},
  schema?: ResponseSchema<T>,
): Promise<T> {
  const { method = 'GET', body, auth = true, signal, idempotencyKey } = opts;

  const send = (token: string | null): Promise<Response> =>
    fetch(`${API_BASE}${path}`, {
      method,
      headers: {
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(idempotencyKey ? { [IDEMPOTENCY_HEADER]: idempotencyKey } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });

  let res = await send(auth ? await ensureAccess() : null);
  if (res.status === 401 && auth) {
    // refreshAccess clears the session only on a definitive rejection (401/403) and preserves
    // it on a transient 429/5xx — so don't clear here, just surface the error.
    const fresh = await refreshAccess();
    if (!fresh) return toError(res);
    res = await send(fresh);
  }
  if (!res.ok) return toError(res);
  if (res.status === 204) return undefined as T;
  const json = await res.json();
  return schema ? schema.parse(json) : (json as T);
}

/**
 * Upload raw bytes (M11 media). Separate from {@link api} because that one JSON-encodes its
 * body: the media endpoints take the object itself, not a multipart envelope, so the browser
 * hands over the canvas-produced `Blob` verbatim.
 *
 * `Content-Length` is set by `fetch` from the Blob, which the server needs — R2 cannot take a
 * stream of unknown size (ADR 0025). Shares the same 401-refresh-and-retry as `api`.
 */
export async function apiUpload<T>(
  path: string,
  blob: Blob,
  contentType: string,
  schema?: ResponseSchema<T>,
): Promise<T> {
  const send = (token: string | null): Promise<Response> =>
    fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: {
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        'content-type': contentType,
      },
      body: blob,
    });

  let res = await send(await ensureAccess());
  if (res.status === 401) {
    const fresh = await refreshAccess();
    if (!fresh) return toError(res);
    res = await send(fresh);
  }
  if (!res.ok) return toError(res);
  const json = await res.json();
  return schema ? schema.parse(json) : (json as T);
}
